import React from 'react';
import { useSelector } from 'react-redux';
import {
  Calculator,
  Ticket,
  CreditCard,
  PieChart,
  TrendingUp,
  IndianRupee,
  Users,
  Activity
} from 'lucide-react';
import { OverviewPageLayout } from '../../components/ui/OverviewPage';

export function FinanceOverviewPage() {
  const financeKPIs = useSelector(state => state.financeReports?.kpis);
  const paymentStats = useSelector(state => state.payments?.statistics);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const quickActions = [
    {
      title: 'Fare Management',
      description: 'Configure pricing, surge rates, and fare calculations',
      icon: Calculator,
      link: '/finance/fares',
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      title: 'Coupons & Discounts',
      description: 'Manage promotional codes and discount campaigns',
      icon: Ticket,
      link: '/finance/coupons',
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Payment Methods',
      description: 'Configure payment gateways and transaction settings',
      icon: CreditCard,
      link: '/finance/payments',
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600'
    },
    {
      title: 'Financial Reports',
      description: 'Generate revenue analytics and financial insights',
      icon: PieChart,
      link: '/finance/reports',
      color: 'bg-orange-50 border-orange-200',
      iconColor: 'text-orange-600'
    }
  ];

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: financeKPIs?.totalRevenue?.current || 0,
      change: financeKPIs?.totalRevenue?.change || 0,
      icon: IndianRupee,
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      title: 'Net Profit',
      value: financeKPIs?.netProfit?.current || 0,
      change: financeKPIs?.netProfit?.change || 0,
      icon: TrendingUp,
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Total Transactions',
      value: paymentStats?.totalTransactions || 0,
      change: financeKPIs?.totalTransactions?.change || 0,
      icon: Activity,
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600',
      isCount: true
    },
    {
      title: 'Average Order Value',
      value: financeKPIs?.averageOrderValue?.current || 0,
      change: financeKPIs?.averageOrderValue?.change || 0,
      icon: Users,
      color: 'bg-orange-50 border-orange-200',
      iconColor: 'text-orange-600'
    }
  ];

  // Summary section for financial overview
  const summarySection = (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Financial Summary</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="text-center">
          <div className="text-3xl font-bold text-green-600">
            {formatCurrency(paymentStats?.totalRevenue || 0)}
          </div>
          <p className="text-sm text-gray-600 mt-1">Total Revenue</p>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-blue-600">
            {formatCurrency(paymentStats?.totalProcessingFees || 0)}
          </div>
          <p className="text-sm text-gray-600 mt-1">Processing Fees</p>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-purple-600">
            {formatCurrency(paymentStats?.netRevenue || 0)}
          </div>
          <p className="text-sm text-gray-600 mt-1">Net Revenue</p>
        </div>
      </div>
    </div>
  );

  return (
    <OverviewPageLayout
      title="Finance & Pricing"
      subtitle="Comprehensive financial management and analytics dashboard"
      kpiCards={kpiCards}
      quickActions={quickActions}
      summarySection={summarySection}
    />
  );
}
